/**
 * collections-api — generic folder/label organization for library objects
 * (messages first; surveys, booking questions, coupons/promos and services
 * reuse the same family-scoped API + hook later).
 *
 * Semantics (operator, 2026-08-04): folders are LABELS — an item may live in
 * several folders; two levels max (folder + subfolder); folders are shared
 * org-wide and writable by the same capability that authors the items.
 *
 * Serving path: apiRaw → serving-engine `/v1/admin/collections` bindings →
 * seed-world `collections.json` (+ write ledger/overlay, so created folders
 * and membership edits survive reload). Formal api-contracts fragment is the
 * standing follow-up ask (queues-api precedent).
 */
import { useEffect, useState } from "react";
import { apiRaw } from "@v0y4ge/api-client";
import { onTenantChange, onTenantSwitching } from "../shell/access";

export interface Folder { id: string; family: string; name: string; parentId: string | null; }
export interface CollectionsState { folders: Folder[]; members: Record<string, string[]>; }

const _cache = new Map<string, CollectionsState | null>();
const _errors = new Map<string, string | null>();
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _cache.clear(); notify(); });
onTenantChange(() => { const fams = Array.from(_cache.keys()); _cache.clear(); fams.forEach((f) => void loadFam(f)); });

async function loadFam(family: string) {
  try {
    const r = await apiRaw<{ collections?: Folder[]; members?: Record<string, string[]> }>(
      `/v1/admin/collections?family=${encodeURIComponent(family)}`, undefined, "listCollections");
    _cache.set(family, { folders: r?.collections ?? [], members: r?.members ?? {} });
    _errors.set(family, null);
  } catch (e) {
    _errors.set(family, String((e as { message?: string })?.message || e));
    if (!_cache.get(family)) _cache.set(family, { folders: [], members: {} });
  }
  notify();
}

export function useCollections(family: string) {
  const [, setV] = useState(0);
  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_cache.has(family)) { _cache.set(family, null); void loadFam(family); }
    return () => { _listeners.delete(fn); };
  }, [family]);

  const st = _cache.get(family) || null;
  // A–Z ordering is the folder order everywhere (tree, cards, breadcrumb, pickers).
  // Sorted here, once, so no consumer can disagree; manual drag-ordering would add
  // a persisted `sort` field and replace this comparator. Family filter is
  // defensive: if the engine binding ever serves the whole document, each
  // consumer still sees only its own family's folders.
  const folders = [...(st?.folders ?? [])].filter((f) => !f.family || f.family === family).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }));
  const members = st?.members ?? {};

  const foldersOf = (itemId: string): string[] =>
    folders.filter((f) => (members[f.id] || []).includes(itemId)).map((f) => f.id);
  /** Item ids in a folder INCLUDING its subfolders (2-level rollup). */
  const rollup = (folderId: string): Set<string> => {
    const out = new Set<string>(members[folderId] || []);
    folders.filter((f) => f.parentId === folderId).forEach((c) => (members[c.id] || []).forEach((id) => out.add(id)));
    return out;
  };

  const mutate = (fn: (s: CollectionsState) => void) => {
    const cur = _cache.get(family);
    if (cur) { fn(cur); notify(); }
  };

  return {
    loading: st === null,
    error: _errors.get(family) ?? null,
    folders, members, foldersOf, rollup,
    refresh: () => void loadFam(family),
    create: async (name: string, parentId: string | null = null): Promise<Folder> => {
      const r = await apiRaw<Folder>("/v1/admin/collections", {
        method: "POST", body: JSON.stringify({ family, name, parentId }),
      }, "createCollection");
      const row: Folder = { id: String(r.id), family, name: r.name ?? name, parentId: r.parentId ?? parentId };
      mutate((s) => { s.folders.push(row); s.members[row.id] = s.members[row.id] || []; });
      return row;
    },
    rename: async (id: string, name: string) => {
      await apiRaw(`/v1/admin/collections/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name }) }, "updateCollection");
      mutate((s) => { const f = s.folders.find((x) => x.id === id); if (f) f.name = name; });
    },
    /**
     * Copy a folder (and its subfolders) as a new sibling. Folders are LABELS, so the
     * copy references the SAME items — nothing is cloned, items simply gain a second home.
     * Composed from create + setItems; no new contract op.
     */
    duplicate: async (id: string): Promise<Folder | null> => {
      const src = folders.find((f) => f.id === id);
      if (!src) return null;
      const taken = new Set(folders.filter((f) => (f.parentId || null) === (src.parentId || null)).map((f) => f.name));
      let name = `${src.name} (copy)`;
      for (let n = 2; taken.has(name); n++) name = `${src.name} (copy ${n})`;

      const api = {
        create: async (nm: string, parentId: string | null) => {
          const r = await apiRaw<Folder>("/v1/admin/collections", {
            method: "POST", body: JSON.stringify({ family, name: nm, parentId }),
          }, "createCollection");
          const row: Folder = { id: String(r.id), family, name: r.name ?? nm, parentId: r.parentId ?? parentId };
          mutate((s) => { s.folders.push(row); s.members[row.id] = s.members[row.id] || []; });
          return row;
        },
        fill: async (fid: string, ids: string[]) => {
          if (!ids.length) return;
          await apiRaw(`/v1/admin/collections/${encodeURIComponent(fid)}/items`, { method: "PUT", body: JSON.stringify({ add: ids }) }, "setCollectionItems");
          mutate((s) => { s.members[fid] = Array.from(new Set([...(s.members[fid] || []), ...ids])); });
        },
      };

      const copy = await api.create(name, src.parentId);
      await api.fill(copy.id, [...(members[id] || [])]);
      for (const kid of folders.filter((f) => f.parentId === id)) {
        const kidCopy = await api.create(kid.name, copy.id);
        await api.fill(kidCopy.id, [...(members[kid.id] || [])]);
      }
      return copy;
    },
    /**
     * Re-parent a folder. `parentId: null` promotes it to the top level.
     * Two-level cap: a folder that HAS subfolders can only live at the top level,
     * and nothing may move into itself or its own child.
     */
    moveFolder: async (id: string, parentId: string | null) => {
      const src = folders.find((f) => f.id === id);
      if (!src || (src.parentId || null) === parentId) return;
      if (parentId) {
        if (parentId === id) throw new Error("A folder cannot be moved into itself.");
        const target = folders.find((f) => f.id === parentId);
        if (!target) throw new Error("That folder no longer exists.");
        if (target.parentId) throw new Error("Folders go two levels deep — pick a top-level folder.");
        if (folders.some((f) => f.parentId === id)) throw new Error("This folder has subfolders, so it can only sit at the top level.");
      }
      await apiRaw(`/v1/admin/collections/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ parentId }) }, "updateCollection");
      mutate((s) => { const f = s.folders.find((x) => x.id === id); if (f) f.parentId = parentId; });
    },
    /** mode "keep": items just lose this label. mode "purge": member items are deleted too. */
    remove: async (id: string, mode: "keep" | "purge") => {
      await apiRaw(`/v1/admin/collections/${encodeURIComponent(id)}?mode=${mode}&family=${encodeURIComponent(family)}`, { method: "DELETE" }, "deleteCollection");
      mutate((s) => {
        const dead = new Set([id, ...s.folders.filter((f) => f.parentId === id).map((f) => f.id)]);
        s.folders = s.folders.filter((f) => !dead.has(f.id));
        dead.forEach((d) => delete s.members[d]);
      });
    },
    setItems: async (id: string, delta: { add?: string[]; remove?: string[] }) => {
      await apiRaw(`/v1/admin/collections/${encodeURIComponent(id)}/items`, { method: "PUT", body: JSON.stringify(delta) }, "setCollectionItems");
      mutate((s) => {
        const cur = new Set(s.members[id] || []);
        (delta.add || []).forEach((x) => cur.add(x));
        (delta.remove || []).forEach((x) => cur.delete(x));
        s.members[id] = Array.from(cur);
      });
    },
  };
}
export type Collections = ReturnType<typeof useCollections>;
